import "./queueFleet.css";
import { api } from "../api";
import { emptyBlock, errorBanner, loadingBlock, pill, tableRegion } from "./shared/blocks";
import { keepFocus } from "./shared/focus";
import { createLifecycle, isAbort } from "./shared/lifecycle";
import { IDEMPOTENCY_HEADER, intentFor, peekIntent, resolveIntent, retryScope } from "./shared/intent";
import { clientHref, formatEpoch, h, safeMessage, tidy } from "./shared/safe";
import {
  JOBS_LIMIT,
  STATUS_FILTERS,
  describeRetryError,
  isDefinitiveRefusal,
  isKnownStatus,
  jobLabel,
  parseFilter,
  parseJobs,
  readRetryAck,
  retryDecision,
  statusTone,
  type Job,
  type JobsPage,
  type Notice,
  type StatusFilter,
} from "./queueModel";

// Queue: recent jobs, filterable by status, with a manual Retry only where one is genuinely allowed.
//
// History: the original page interpolated client names, ids, statuses and server errors straight into
// innerHTML and filtered a single 200-row response in the browser. This version
//  - builds every element with h() (data becomes text or a validated attribute, never markup);
//  - asks the server for the filter (`/v1/jobs?status=...`, the endpoint's existing parameter) and labels
//    what it shows as loaded/recent rows, because the endpoint returns at most 200 and is not a total;
//  - offers Retry only for dead jobs that are not parked with an unknown outcome (there is no
//    acknowledgement shortcut: acknowledging is not evidence that nothing was posted);
//  - shows 403/409/422/500/502 and enqueue failures inline and never says a retry worked unless the server
//    said `requeued: true`, then always re-reads the real state;
//  - ignores responses that arrive after a newer request or after the person left the page.

const FILTER_KEY = "cloudpush_queue_filter";
let rememberedFilter: StatusFilter = "all"; // fallback when sessionStorage is unavailable

function loadFilter(): StatusFilter {
  try {
    return parseFilter(sessionStorage.getItem(FILTER_KEY));
  } catch {
    return rememberedFilter;
  }
}
function saveFilter(filter: StatusFilter) {
  rememberedFilter = filter;
  try {
    sessionStorage.setItem(FILTER_KEY, filter);
  } catch {
    /* storage blocked: the in-memory copy still survives navigation within this session */
  }
}

const FILTER_LABELS: Record<StatusFilter, string> = { all: "All", queued: "Queued", running: "Running", failed: "Failed", dead: "Dead", done: "Done" };

export async function queueView(root: HTMLElement): Promise<void> {
  const shell = h("div", { class: "qf qf-queue" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);

  let filter = loadFilter();
  let jobs: Job[] = [];
  let hasData = false;
  let dropped = 0;
  let loading = false;
  let page: JobsPage | null = null;
  let loadError: string | null = null;
  let loadedAt: Date | null = null;
  let clientNames = new Map<string, string>();
  const pending = new Set<string>();
  const notices = new Map<string, Notice>();

  // ---- static frame -----------------------------------------------------------------------------------
  const refreshBtn = h("button", { type: "button", class: "secondary" }, "Refresh");
  const filterBar = h("div", { class: "btn-group qf-filters", role: "group", "aria-label": "Filter jobs by status" });
  const summary = h("p", { class: "qf-summary", role: "status" });
  // Two live regions, present from the start so assistive technology announces what is placed in them.
  const statusHost = h("div", { role: "status", class: "qf-live" });
  const alertHost = h("div", { role: "alert", class: "qf-live" });
  const jobsHeading = h("h2", { class: "qf-sr-only", tabindex: "-1" }, "Job list");
  const content = h("div", { class: "qf-content" });
  shell.append(
    h("div", { class: "topbar" }, h("h1", null, "Queue"), refreshBtn),
    filterBar,
    summary,
    statusHost,
    alertHost,
    jobsHeading,
    content,
  );

  // ---- painting ---------------------------------------------------------------------------------------
  function paintFilters() {
    keepFocus(filterBar, () => filterBar.replaceChildren(
      ...STATUS_FILTERS.map((f) =>
        h("button", { type: "button", class: f === filter ? "" : "secondary", "aria-pressed": String(f === filter), dataset: { focus: `filter-${f}` }, on: { click: () => setFilter(f) } }, FILTER_LABELS[f]),
      ),
    ));
  }

  function paintSummary() {
    if (!hasData) {
      summary.textContent = "";
      return;
    }
    const scope = filter === "all" ? "" : ` with status "${filter}"`;
    // The server's own `page.mayBeTruncated` wins; without it, reaching the legacy cap is the only signal.
    const limit = page?.limit ?? JOBS_LIMIT;
    const limited = page?.mayBeTruncated ?? jobs.length + dropped >= limit;
    const parts = [`Loaded ${jobs.length} recent ${jobs.length === 1 ? "job" : "jobs"}${scope}.`];
    if (limited) {
      parts.push(`This is the server's ${limit}-job limit, not a total: older jobs, or more jobs for other clients, may exist that are not shown.`);
    } else if (filter === "all") {
      parts.push("These are all the jobs the server returned.");
    }
    if (dropped > 0) parts.push(`${dropped} unreadable ${dropped === 1 ? "row was" : "rows were"} skipped.`);
    if (filter === "all" && jobs.length > 0) {
      const tally = new Map<string, number>();
      for (const j of jobs) {
        const key = isKnownStatus(j.status) ? j.status : "unrecognized";
        tally.set(key, (tally.get(key) ?? 0) + 1);
      }
      parts.push(`Among the loaded jobs: ${[...tally].map(([s, n]) => `${n} ${s}`).join(", ")}.`);
    }
    if (loadedAt) parts.push(`Loaded at ${loadedAt.toLocaleTimeString()}.`);
    summary.textContent = parts.join(" ");
  }

  function showNotice(jobId: string, notice: Notice) {
    const text = `Job ${tidy(jobId, 16)}: ${notice.message}`;
    const card = h("div", { class: `qf-notice qf-notice--${notice.tone}` }, h("span", { class: "qf-notice__text" }, text), h("button", { type: "button", class: "secondary qf-dismiss", on: { click: () => { statusHost.replaceChildren(); alertHost.replaceChildren(); } } }, "Dismiss"));
    statusHost.replaceChildren();
    alertHost.replaceChildren();
    (notice.tone === "error" ? alertHost : statusHost).append(card);
  }

  function actionCell(job: Job): HTMLElement {
    const decision = retryDecision(job);
    const cell = h("td", { dataset: { column: "action" } });
    const shortId = tidy(job.id ?? "", 16);
    switch (decision.kind) {
      case "offer": {
        const busy = job.id !== null && pending.has(job.id);
        cell.append(
          h(
            "button",
            {
              type: "button",
              class: `secondary retry-btn${busy ? " is-pending" : ""}`,
              "aria-label": `Retry ${jobLabel(job)}, job ${shortId}`,
              "aria-busy": busy ? "true" : null,
              "aria-disabled": busy ? "true" : null,
              dataset: { focus: `retry-${job.id}`, jobId: job.id ?? undefined },
              on: { click: () => void retry(job) },
            },
            busy ? "Retrying…" : "Retry",
          ),
        );
        if (job.id !== null && peekIntent(retryScope(job.id))) cell.append(h("p", { class: "qf-reason", dataset: { sameRequest: "true" } }, "An earlier attempt has no confirmed result. Retry repeats the same request, so it cannot queue the job twice."));
        break;
      }
      case "blocked_unknown":
      case "blocked_confirmed": {
        const confirmed = decision.kind === "blocked_confirmed";
        cell.append(h("div", { class: "qf-blocked" }, pill(confirmed ? "Blocked: effect confirmed" : "Blocked: outcome unknown", "warn"), h("p", { class: "qf-reason" }, decision.reason)));
        if (decision.code) cell.dataset.retryCode = decision.code;
        if (decision.machineReason) cell.dataset.retryReason = decision.machineReason;
        break;
      }
      case "automatic":
        cell.append(h("div", { class: "qf-blocked" }, h("span", { class: "muted" }, "Retrying automatically"), h("p", { class: "qf-reason" }, decision.reason)));
        if (decision.code) cell.dataset.retryCode = decision.code;
        if (decision.machineReason) cell.dataset.retryReason = decision.machineReason;
        break;
      case "server_denied":
        cell.append(h("div", { class: "qf-blocked" }, pill("Retry unavailable", "neutral"), h("p", { class: "qf-reason" }, decision.reason), decision.requiresReconciliation ? h("p", { class: "qf-reason" }, "Reconciliation is required before this can be retried.") : null));
        if (decision.requiresReconciliation && job.id) cell.append(h("button",{type:"button",class:"secondary",disabled:pending.has(job.id),on:{click:()=>void reconcile(job)}},"Reconcile evidence"));
        if (decision.code) cell.dataset.retryCode = decision.code;
        if (decision.machineReason) cell.dataset.retryReason = decision.machineReason;
        break;
      case "malformed":
        cell.append(h("div", { class: "qf-blocked" }, pill("Retry status unavailable", "neutral"), h("p", { class: "qf-reason" }, decision.reason)));
        cell.dataset.retryCode = "MALFORMED_METADATA";
        break;
      case "no_id":
        cell.append(h("p", { class: "qf-reason" }, decision.reason));
        break;
      default:
        cell.append(h("span", { class: "muted" }, "—"));
    }
    const inline = job.id !== null ? notices.get(job.id) : undefined;
    if (inline) cell.append(h("p", { class: `qf-inline qf-inline--${inline.tone}` }, inline.message));
    return cell;
  }

  function jobRow(job: Job): HTMLElement {
    const created = formatEpoch(job.createdAt);
    const clientName = job.clientId !== null ? clientNames.get(job.clientId) : undefined;
    const known = isKnownStatus(job.status);
    return h(
      "tr",
      { dataset: { jobId: job.id ?? undefined } },
      h(
        "td",
        null,
        h("div", { class: "qf-strong qf-wrap" }, jobLabel(job)),
        job.id ? h("div", { class: "muted qf-mono qf-wrap" }, `Job ${tidy(job.id, 40)}`) : h("div", { class: "muted" }, "No job id"),
        job.lastError && (job.status === "failed" || job.status === "dead") ? h("div", { class: "qf-error-detail qf-wrap" }, `Last error: ${safeMessage(job.lastError, 200)}`) : null,
      ),
      h("td", { dataset: { priority: "mid" } }, job.clientId !== null ? h("a", { href: clientHref(job.clientId), class: "qf-wrap" }, clientName ? tidy(clientName, 80) : tidy(job.clientId, 40)) : "—"),
      h("td", null, pill(known ? job.status : tidy(job.status, 24), known ? statusTone(job.status) : "neutral"), known ? null : h("div", { class: "muted qf-small" }, "Unrecognized status"), job.dispatch === "pending" ? h("div", { class: "muted qf-small", dataset: { dispatch: "pending" } }, "Accepted; waiting for the dispatcher to send it to the queue. Not a failure, not delivered.") : null),
      h("td", { dataset: { priority: "low" } }, job.attempts === null ? "—" : String(job.attempts)),
      h("td", { dataset: { priority: "low" } }, created ? h("time", { datetime: created.iso, title: created.iso }, created.text) : h("span", { class: "muted" }, "Unknown")),
      actionCell(job),
    );
  }

  function buildTable(): HTMLElement {
    const th = (label: string, priority?: string) => h("th", { scope: "col", dataset: { priority } }, label);
    const table = h(
      "table",
      { class: "qf-table" },
      h("caption", { class: "qf-sr-only" }, `Recent jobs${filter === "all" ? "" : ` with status ${filter}`}, newest first. ${jobs.length} loaded.`),
      h("thead", null, h("tr", null, th("Type"), th("Client", "mid"), th("Status"), th("Attempts", "low"), th("Created", "low"), th("Action"))),
      h("tbody", null, ...jobs.map(jobRow)),
    );
    return tableRegion("Jobs", table);
  }

  function paintContent() {
    const parts: (Node | null)[] = [];
    if (!hasData && loading) parts.push(loadingBlock("Loading jobs"));
    else if (!hasData) parts.push(errorBanner(loadError ?? "The jobs could not be loaded.", { retry: () => void load() }));
    else {
      if (loadError) parts.push(errorBanner(`Couldn't refresh: ${loadError} Showing the last results that loaded${loadedAt ? ` at ${loadedAt.toLocaleTimeString()}` : ""}.`, { retry: () => void load() }));
      if (jobs.length === 0) {
        parts.push(
          filter === "all"
            ? emptyBlock("No jobs loaded", "The server returned no jobs. Jobs appear here when content is generated or deployed.")
            : emptyBlock(`No ${filter} jobs among the recent results`, "Try another status, or refresh.", [h("button", { type: "button", class: "secondary", on: { click: () => setFilter("all") } }, "Show all statuses")]),
        );
      } else {
        const table = buildTable();
        if (loading) table.setAttribute("aria-busy", "true");
        table.classList.toggle("qf-refreshing", loading);
        parts.push(table);
      }
    }
    keepFocus(content, () => content.replaceChildren(...parts.filter((p): p is Node => p !== null)), jobsHeading);
    refreshBtn.toggleAttribute("aria-busy", loading);
  }

  const paintAll = () => {
    paintFilters();
    paintSummary();
    paintContent();
  };

  // ---- data -------------------------------------------------------------------------------------------
  async function loadClientNames() {
    try {
      const { clients } = await api<{ clients: unknown }>("/v1/clients");
      if (!life.alive() || !Array.isArray(clients)) return;
      const names = new Map<string, string>();
      for (const c of clients) {
        if (typeof c === "object" && c !== null && typeof (c as Record<string, unknown>).id === "string" && typeof (c as Record<string, unknown>).business_name === "string") {
          names.set((c as Record<string, string>).id, (c as Record<string, string>).business_name);
        }
      }
      clientNames = names;
      paintContent();
    } catch {
      /* names are a nicety: rows fall back to the client id */
    }
  }

  async function load(): Promise<void> {
    const req = life.begin();
    loading = true;
    paintAll();
    try {
      const path = filter === "all" ? "/v1/jobs" : `/v1/jobs?status=${encodeURIComponent(filter)}`;
      const body = await api<unknown>(path, { signal: req.signal });
      if (!req.current()) return;
      const parsed = parseJobs(body);
      if (!parsed) throw new Error("The server's reply was not in the expected format.");
      jobs = parsed.jobs;
      // A job the server now shows as settled (not dead) has no unresolved retry intention any more.
      for (const j of jobs) if (j.id !== null && j.status !== "dead") resolveIntent(retryScope(j.id));
      dropped = parsed.dropped;
      page = parsed.page;
      hasData = true;
      loadError = null;
      loadedAt = new Date();
    } catch (e) {
      if (isAbort(e) || !req.current()) return;
      loadError = safeMessage((e as Error).message || "The jobs could not be loaded.");
    }
    if (req.current()) {
      loading = false;
      paintAll();
    }
  }

  function setFilter(next: StatusFilter) {
    if (next === filter) return;
    filter = next;
    saveFilter(next);
    // The previous filter's rows are not this filter's rows: drop them so they can't be mistaken for results.
    hasData = false;
    loadError = null;
    void load();
  }

  async function reconcile(job:Job) {
    const id=job.id;if(!id || pending.has(id)) return;
    pending.add(id);paintContent();
    let n:Notice;
    try {const r=await api<{ok:boolean,retried:boolean}>(`/v1/jobs/${encodeURIComponent(id)}/reconcile`,{method:"POST"});n=r.ok && r.retried===false ? {tone:"success",message:"The recorded receipt settles this job. No provider write was repeated."} : {tone:"warning",message:"Evidence did not settle this operation. It stays blocked."};}
    catch(e) {n={tone:"warning",message:safeMessage(e)};}
    pending.delete(id);if(!life.alive()) return;notices.set(id,n);showNotice(id,n);await load();
  }

  async function retry(job: Job) {
    const id = job.id;
    if (id === null || pending.has(id)) return; // duplicate submission while one is in flight
    pending.add(id);
    const scope = retryScope(id);
    const intent = intentFor(scope);
    notices.delete(id);
    statusHost.replaceChildren();
    alertHost.replaceChildren();
    paintContent();

    let outcome: Notice & { refresh: boolean };
    try {
      // No body, and in particular no acknowledgement flag: an unknown outcome cannot be waved through.
      // One Idempotency-Key per intention: reused only if an earlier attempt of THIS retry ended without a readable answer.
      const reply = await api<unknown>(`/v1/jobs/${encodeURIComponent(id)}/retry`, { method: "POST", headers: { [IDEMPOTENCY_HEADER]: intent.key } });
      const ack = readRetryAck(reply, id);
      if (ack) {
        resolveIntent(scope);
        outcome = {
          tone: "success",
          refresh: true,
          message: `${ack.replayed ? "Safe replay: the server recognised the earlier request and returned its original result; nothing was queued twice. " : ""}${
            ack.dispatch === "pending" ? "Accepted and durably recorded. The send to the queue is still pending and the dispatcher will complete it. This is not a failure, and nothing has been delivered yet." : "Re-queued. Nothing has been delivered yet."
          } The list below is refreshed from the server.`,
        };
      } else {
        // A 200 that is not a recognisable acknowledgement is an uncertain answer, not a success. The key is kept.
        outcome = { tone: "warning", refresh: true, message: "The server replied, but not with a confirmation this page recognises, so it is not known whether the job was re-queued. The list below shows its actual state. Retry repeats the same request with the same key." };
      }
    } catch (e) {
      if (isDefinitiveRefusal(e)) resolveIntent(scope);
      outcome = describeRetryError(e);
    }
    pending.delete(id);
    if (!life.alive()) return; // the person moved on; do not paint into another view
    notices.set(id, outcome);
    showNotice(id, outcome);
    paintContent();
    if (outcome.refresh) await load(); // a job can change state between render and click: read the truth again
  }

  refreshBtn.addEventListener("click", () => void load());

  paintAll();
  void loadClientNames();
  await load();
}
